import React, { useState, useEffect, useCallback } from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../../text';
import {theme} from '../../constants';
import {components} from '../../components';
import {hooks} from '../../hooks';
import {useLanguage} from '../../context/LanguageContext';
import { MdLocationOn, MdPerson, MdDelete, MdAssignment, MdGroups, MdEvent, MdFactCheck } from 'react-icons/md';
import InfiniteScroll from 'react-infinite-scroll-component';
import axiosInstance from '../../services/axiosInstance';
import {RootState} from '../../store';

const LocationIcon = MdLocationOn as any;
const PersonIcon = MdPerson as any;
const DeleteIcon = MdDelete as any;
const AssignmentIcon = MdAssignment as any;
const GroupsIcon = MdGroups as any;
const EventIcon = MdEvent as any;
const FactCheckIcon = MdFactCheck as any;

export const Activity: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const {pathname} = useLocation();
  const { t, language } = useLanguage();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);

  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedId, setExpandedId] = useState<any>(null);
  const [activityToDelete, setActivityToDelete] = useState<number | null>(null);
  const [imageIndices, setImageIndices] = useState<Record<string, number>>({});

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  // Scroll to top saat pertama kali dimuat
  React.useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
  }, [pathname]);

  const fetchActivities = useCallback(async (pageNumber: number, isRefresh = false) => {
    try {
      if (pageNumber === 1) setIsLoading(true);
      const response = await axiosInstance.get(`/activities?page=${pageNumber}&limit=5`);
      const newActivities = response.data || [];

      if (isRefresh) {
        setActivities(newActivities);
      } else {
        setActivities((prev) => {
          const existingIds = new Set(prev.map(a => a.id));
          const uniqueNew = newActivities.filter((a: any) => !existingIds.has(a.id));
          return [...prev, ...uniqueNew];
        });
      }
      
      setHasMore(newActivities.length === 5);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchActivities(1, true);
  }, [fetchActivities]);

  const getTimeAgo = (datetime: string) => {
    const now = new Date();
    const past = new Date(datetime);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return t.activity?.justNow || 'Just now';
    } else if (diffInSeconds < 3600) { // Less than an hour
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      return `${diffInMinutes} ${t.activity?.minsAgo || 'mins ago'}`;
    } else if (diffInSeconds < 86400) { // Less than a day
      const diffInHours = Math.floor(diffInSeconds / 3600);
      return `${diffInHours} ${t.activity?.hoursAgo || 'hours ago'}`;
    } else if (diffInSeconds < 2592000) { // Less than a month (30 days)
      const diffInDays = Math.floor(diffInSeconds / 86400);
      return `${diffInDays} ${t.activity?.daysAgo || 'days ago'}`;
    } else if (diffInSeconds < 31536000) { // Less than a year (365 days)
      const diffInMonths = Math.floor(diffInSeconds / 2592000);
      return `${diffInMonths} ${t.activity?.monthsAgo || 'months ago'}`;
    } else {
      const diffInYears = Math.floor(diffInSeconds / 31536000);
      return `${diffInYears} ${t.activity?.yearsAgo || 'years ago'}`;
    }
  };

  const handleImageScroll = useCallback((e: React.UIEvent<HTMLDivElement>, activityId: any) => {
    const container = e.currentTarget;
    const index = Math.round(container.scrollLeft / container.clientWidth);
    setImageIndices(prev => {
      if (prev[activityId] === index) return prev;
      return { ...prev, [activityId]: index };
    });
  }, []);

  const scrollToImage = useCallback((activityId: any, index: number) => {
    const container = document.getElementById(`image-container-${activityId}`);
    if (container) {
      container.scrollTo({
        left: container.clientWidth * index,
        behavior: 'smooth'
      });
      setImageIndices(prev => ({ ...prev, [activityId]: index }));
    }
  }, []);

  const executeDelete = async () => {
    if (activityToDelete === null) return;
    
    try {
      await axiosInstance.delete(`/activities/${activityToDelete}`);
      setActivities((prev) => prev.filter((a) => a.id !== activityToDelete));
    } catch (error) {
      console.error('Failed to delete activity:', error);
    } finally {
      setActivityToDelete(null);
    }
  };

  const renderDeleteModal = () => {
    if (activityToDelete === null) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 320, textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <text.H4 style={{marginBottom: 12}}>{t.activity?.deleteTitle || 'Delete Activity'}</text.H4>
          <text.T16 style={{marginBottom: 24, color: theme.colors.bodyTextColor}}>
            {t.activity?.deleteConfirm || 'Are you sure you want to delete this activity?'}
          </text.T16>
          <div style={{display: 'flex', gap: 12}}>
            <components.Button title={t.common?.cancel || 'Cancel'} style={{backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10 }} onClick={() => setActivityToDelete(null)} />
            <components.Button title={t.common?.delete || 'Delete'} style={{backgroundColor: '#EF4444', padding: 10 }} onClick={executeDelete} />
          </div>
        </div>
      </div>
    );
  };

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title={t.activity?.title || "Activity"}
        user={true}
      />
    );
  };

  const renderActivityCard = (activity: any): JSX.Element => {
    const dateStr = new Date(activity.datetime).toLocaleString(language === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const timeAgo = getTimeAgo(activity.datetime);
    const isExpanded = expandedId === activity.id;

    return (
      <div
        key={activity.id}
        style={{
          backgroundColor: theme.colors.white, borderRadius: 16, padding: 16, marginBottom: 16,
          boxShadow: '0px 6px 20px rgba(18, 26, 51, 0.04)', border: '1px solid #F3F4F6',
          display: 'flex', flexDirection: 'column', cursor: 'pointer'
        }}
        onClick={() => {
          if (activity.attachment_url) {
            setExpandedId(isExpanded ? null : activity.id);
          }
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <text.H5 style={{ textTransform: 'capitalize', flex: 1, paddingRight: 8, fontSize: 15 }} numberOfLines={2}>{activity.title}</text.H5>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <text.T14 style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap', marginTop: 2 }}>{dateStr}</text.T14>
            {activity.user_id == authSlice?.userId && (
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  setActivityToDelete(activity.id);
                }}
                style={{ cursor: 'pointer', display: 'flex', padding: 2 }}
              >
                <DeleteIcon size={18} color="#EF4444" />
              </div>
            )}
          </div>
        </div>
        <text.T14 style={{ color: theme.colors.bodyTextColor, lineHeight: 1.5, marginBottom: 10, display: 'block', whiteSpace: 'pre-wrap' }}>{activity.description}</text.T14>
        
        {/* Media / Image */}
        {(() => {
          let imageUrls: string[] = [];
          if (activity.attachment_url) {
            try {
              imageUrls = JSON.parse(activity.attachment_url);
              if (!Array.isArray(imageUrls)) imageUrls = [activity.attachment_url];
            } catch (e) {
              imageUrls = [activity.attachment_url];
            }
          }
          
          if (imageUrls.length === 0) return null;

          return (
            <div style={{ margin: '0 -16px 12px -16px', position: 'relative' }}>
              <div 
                id={`image-container-${activity.id}`}
                onScroll={(e) => handleImageScroll(e, activity.id)}
                style={{ 
                  display: 'flex', 
                  overflowX: 'auto', 
                  scrollSnapType: 'x mandatory',
                  maxHeight: isExpanded ? 1000 : 140, 
                  transition: 'max-height 0.4s ease-in-out',
                  alignItems: 'flex-start'
                }}
              >
                {imageUrls.map((url, idx) => (
                  <img 
                    key={idx}
                    src={`${imageBaseUrl}${url}`} 
                    alt={`${activity.title} - ${idx + 1}`} 
                    style={{ 
                      width: '100%', 
                      flexShrink: 0,
                      scrollSnapAlign: 'start',
                      minHeight: 140,
                      maxHeight: isExpanded ? 1000 : 140, 
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: 0,
                      transition: 'max-height 0.4s ease-in-out'
                    }} 
                  />
                ))}
              </div>
              {imageUrls.length > 1 && (
                 <div style={{ position: 'absolute', top: 10, right: 10, backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 8px', borderRadius: 12, fontSize: 11, fontWeight: 'bold', pointerEvents: 'none' }}>
                    {(imageIndices[activity.id] || 0) + 1} / {imageUrls.length}
                 </div>
              )}
              {imageUrls.length > 1 && (
                 <div style={{ display: 'flex', justifyContent: 'center', gap: 6, position: 'absolute', bottom: 10, left: 0, right: 0 }}>
                   {imageUrls.map((_, idx) => (
                     <div
                       key={idx}
                       onClick={(e) => {
                         e.stopPropagation();
                         scrollToImage(activity.id, idx);
                       }}
                       style={{
                         width: 8,
                         height: 8,
                         borderRadius: '50%',
                         backgroundColor: (imageIndices[activity.id] || 0) === idx ? theme.colors.mainColor : 'rgba(255,255,255,0.6)',
                         cursor: 'pointer',
                         transition: 'background-color 0.3s',
                         boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                       }}
                     />
                   ))}
                 </div>
              )}
            </div>
          );
        })()}

        {/* Footer Info: Duration & Location */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {activity.user_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 5px', backgroundColor: '#F3F4F6', borderRadius: 6, border: '1px solid #E5E7EB' }}>
              <PersonIcon size={14} color="#6B7280" />
              <text.T14 style={{ fontSize: 11, color: '#6B7280', fontWeight: '600', textTransform: 'capitalize' }}>{activity.user_name}</text.T14>
            </div>
          )}
          <div style={{ display: 'inline-block', padding: '2px 5px', backgroundColor: '#F9FAFB', borderRadius: 6, border: '1px solid #E5E7EB' }}>
            <text.T14 style={{ fontSize: 11, color: '#6B7280', fontWeight: '600' }}>⏱ {timeAgo}</text.T14>
          </div>
          {activity.address && (
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 5px', backgroundColor: '#EFF6FF', borderRadius: 6, border: '1px solid #BFDBFE', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation(); // Mencegah onClick card ikut terpanggil
                if (activity.location) {
                  window.open(`https://www.google.com/maps/search/?api=1&query=${activity.location}`, '_blank');
                }
              }}
            >
              <LocationIcon size={14} color="#3B82F6" />
              <text.T14 style={{ fontSize: 11, color: '#3B82F6', fontWeight: '600' }}>{activity.address}</text.T14>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMenu = () => {
    const menus = [
      { id: 'survey', label: t.activity?.survey || 'Survey', icon: <AssignmentIcon size={32} color="#3B82F6" />, bg: '#EFF6FF', route: '/SurveyActivity' },
      { id: 'meeting', label: t.activity?.marketing || 'Marketing', icon: <GroupsIcon size={32} color="#10B981" />, bg: '#ECFDF5', route: '/MarketingActivity' },
      { id: 'event', label: (t as any).eventActivity?.menuLabel || 'Event', icon: <EventIcon size={32} color="#6366F1" />, bg: '#EEF2FF', route: '/EventActivity' },
      { id: 'precheck', label: (t as any).preCheckReport?.title || 'Pre-Check', icon: <FactCheckIcon size={32} color="#0891B2" />, bg: '#ECFEFF', route: '/PreCheckReports' },
    ];

    return (
      <div style={{ marginBottom: 24, marginTop: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {menus.map(menu => (
            <div
              key={menu.id}
              onClick={() => navigate(menu.route)}
              style={{
                backgroundColor: theme.colors.white,
                borderRadius: 16,
                padding: '20px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                border: '1px solid #F3F4F6',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 60, height: 60, borderRadius: 16,
                backgroundColor: menu.bg,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                marginBottom: 12
              }}>
                {menu.icon}
              </div>
              <text.H5 style={{ color: theme.colors.mainDark, textAlign: 'center' }}>
                {menu.label}
              </text.H5>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <main style={{ marginTop: 72, paddingBottom: 90 }} className='container'>
          <text.T16 style={{ textAlign: 'center', marginTop: 40, color: '#9CA3AF' }}>{t.common.loading}</text.T16>
        </main>
      );
    }

    return (
      <main
        id="scrollableDiv"
        style={{
          marginTop: 72,
          height: 'calc(100vh - 72px)',
          overflowY: 'auto',
          paddingBottom: 90,
        }}
        className='container'
      >
        {renderMenu()}

        {/* <div style={{ marginBottom: 16 }}>
          <text.H5 style={{ color: theme.colors.mainDark }}>{t.activity?.recent || 'Recent Activities'}</text.H5>
        </div>

        <div style={{marginBottom: 20}}>
          {activities.length === 0 ? (
            <div style={{textAlign: 'center', marginTop: 40, opacity: 0.5}}>
              <text.T16 style={{marginTop: 10}}>{t.activity?.noActivities || 'No activities found.'}</text.T16>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={activities.length}
              next={() => {
                const nextPage = page + 1;
                setPage(nextPage);
                fetchActivities(nextPage, false);
              }}
              hasMore={hasMore}
              loader={
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 10, color: '#9CA3AF'}}>
                  <text.T14>{t.activity?.loadingMore || 'Loading more...'}</text.T14>
                </div>
              }
              scrollableTarget="scrollableDiv"
              endMessage={
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 10, color: '#9CA3AF', marginBottom: 20}}>
                  <text.T14>{t.activity?.allLoaded || 'All activities loaded'}</text.T14>
                </div>
              }
              style={{ overflow: 'visible' }}
            >
              {activities.map((activity) => renderActivityCard(activity))}
            </InfiniteScroll>
          )}
        </div> */}
      </main>
    );
  };

  const renderBottomTabBar = () => {
    return <components.BottomTabBar />;
  };

  return (
    <div id='screen'>
      <style>{`
        ::-webkit-scrollbar {
          width: 0px;
          background: transparent;
          display: none;
        }
        html, body, * {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}</style>
      {renderHeader()}
      {renderContent()}
      {renderBottomTabBar()}
      {renderDeleteModal()}
    </div>
  );
};